import { motion } from "framer-motion";
import parse from "html-react-parser";
import dynamic from "next/dynamic";
import Image from "next/image";
import useTranslation from "next-translate/useTranslation";
import type { IProduct } from "../types/products";
import { Breadcrumb } from "./breadcrumb";
import { ProductFunctionsGrid } from "./product-functions-grid";
import { ProductHero } from "./product-hero";
import { ProductTechnicalDetails } from "./product-technical-details";

const React360Viewer = dynamic(
  () => import("react-360-product-viewer").then((m) => m.React360Viewer),
  { ssr: false }
);

interface IProductCard {
  producer?: string;
  product: IProduct;
}

export const PvcWindowContent = ({ product, producer }: IProductCard) => {
  const { t } = useTranslation("common");
  const additionalOptions = product.en?.PVCDetails?.additionalOptions?.length
    ? product.en.PVCDetails.additionalOptions
    : (product.PVCDetails?.additionalOptions ?? []);
  const image3dUrl =
    product.PVCDetails?.image3d?.sourceUrl ||
    product.en?.PVCDetails?.image3d?.sourceUrl;
  const productFeatures =
    product.PVCDetails?.productFeatures ||
    product.en?.PVCDetails?.productFeatures;
  const content = product.content || product.en?.content;

  return (
    <div>
      <Breadcrumb product={product.title ?? undefined} />
      <h1 className="mb-2 bg-gradient-to-r from-red to-orange p-1 font-bold text-3xl text-white">
        {producer ? <span className="uppercase">{producer}</span> : null}{" "}
        {product.title ?? product.en?.title}
      </h1>
      <ProductHero producer={producer} product={product} />
      {product.title === "Elegant Abstract 76" && (
        <div style={{ textAlign: "center" }}>
          <React360Viewer
            imageFilenamePrefix={""}
            imagesBaseUrl="/img/360/"
            imagesCount={37}
            imagesFiletype="png"
            mouseDragSpeed={20}
            width={640}
          />
        </div>
      )}
      <ProductFunctionsGrid product={product} />
      <div className="my-8">{content && parse(content)}</div>
      <div className="grid grid-cols-1 whitespace-pre md:grid-cols-2">
        <div>
          <ProductTechnicalDetails product={product} />
          <br />
          <br />
          <strong className="uppercase">{t("optionalEqipment")}</strong>
          <br />
          <br />
          <div className="flex flex-row whitespace-normal">
            {additionalOptions.map((option: string) => (
              <div className="w-36" key={option}>
                <motion.div
                  className="p-2"
                  transition={{
                    damping: 10,
                    stiffness: 400,
                    type: "spring",
                  }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Image
                    alt={option}
                    height={150}
                    src={`/img/pvc/options/${option}.jpg`}
                    width={150}
                  />
                </motion.div>
                <div className="break-normal text-center text-xs">
                  {t(`additionalOptions.${option}`)}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          {image3dUrl && (
            <Image
              alt={`${product.title} 3D`}
              height={1000}
              src={image3dUrl}
              style={{ height: "auto", width: "100%" }}
              width={1000}
            />
          )}
        </div>
      </div>
      <div className="product-features my-10">
        <strong className="uppercase">{t("productFeatures")}</strong>
        <div>{productFeatures && parse(productFeatures)}</div>
      </div>
    </div>
  );
};
